import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

import {
  machineCatalog as machineCatalogApi,
  prospects as prospectsApi,
  quotes as quotesApi,
} from "@/api";
import { formatApiError } from "@/lib/api";

const VAT_RATE = 20;

const PAYMENT_METHOD_OPTIONS = [
  ["", "À définir"],
  ["bank_transfer", "Virement"],
  ["card", "Carte"],
  ["cheque", "Chèque"],
  ["cash", "Espèces"],
  ["bank_financing", "Financement bancaire"],
  ["leasing", "Leasing"],
  ["third_party", "Tiers financeur"],
  ["other", "Autre"],
];

const BUSINESS_PROCESS_OPTIONS = [
  ["goods", "Biens"],
  ["services", "Services"],
  ["mixed", "Biens et services"],
];

const SERVICE_OPTIONS = [
  "Livraison",
  "Formation",
  "Garantie 12 mois",
  "Marketing Kit",
  "Autre",
];

const emptyProductLine = () => ({
  catalog_item_id: "",
  section: "Produit Principal",
  reference: "",
  description: "",
  warranty_months: "",
  quantity: "1",
  unit_price: "",
  discount_rate: "0",
  sales_classification_code: "",
  purchase_unit_price: "0",
});

const emptyHandpieceLine = () => ({
  catalog_item_id: "",
  section: "Pièces à Main",
  reference: "",
  description: "",
  warranty_months: "",
  quantity: "1",
  unit_price: "",
  discount_rate: "0",
  sales_classification_code: "",
  purchase_unit_price: "0",
});

const emptyServiceLine = () => ({
  section: "Prestations incluses",
  service: "",
  custom_description: "",
  unit_price: "0",
  discount_rate: "0",
  sales_classification_code: "",
  purchase_unit_price: "0",
});

const prospectLabel = (prospect) =>
  prospect?.company ||
  `${prospect?.firstName || ""} ${prospect?.lastName || ""}`.trim() ||
  prospect?.email ||
  prospect?.id ||
  "Prospect";

const sectionText = (value) =>
  String(value || "")
    .trim()
    .toLocaleLowerCase("fr-FR");

const opportunityInitialLines = (opportunity) => {
  const items = Array.isArray(opportunity?.items) ? opportunity.items : [];

  const productLines = [];
  const handpieceLines = [];
  const serviceLines = [];

  items.forEach((item) => {
    const section = sectionText(item.section);

    if (section.includes("pièce") || section.includes("piece")) {
      handpieceLines.push({
        ...emptyHandpieceLine(),
        catalog_item_id: item.catalogItemId || "",
        reference: item.reference || "",
        description: item.description || "",
        warranty_months: String(
          item.warrantyMonths ?? item.warranty_months ?? ""
        ),
        quantity: String(item.quantity || 1),
        unit_price: String(item.unitPrice ?? item.unit_price ?? ""),
        discount_rate: String(
          item.discountRate ?? item.discount_rate ?? 0
        ),
        sales_classification_code:
          item.salesClassificationCode ??
          item.sales_classification_code ??
          "",
        purchase_unit_price: String(
          item.purchaseUnitPrice ??
          item.purchase_unit_price ??
          0
        ),
      });
      return;
    }

    if (
      section.includes("prestation") ||
      section.includes("service") ||
      section.includes("formation")
    ) {
      const description = String(item.description || "").trim();
      const knownService = SERVICE_OPTIONS.includes(description)
        ? description
        : description
          ? "Autre"
          : "";

      serviceLines.push({
        ...emptyServiceLine(),
        service: knownService,
        custom_description:
          knownService === "Autre" ? description : "",
        unit_price: String(item.unitPrice ?? item.unit_price ?? 0),
        discount_rate: String(
          item.discountRate ?? item.discount_rate ?? 0
        ),
        sales_classification_code:
          item.salesClassificationCode ??
          item.sales_classification_code ??
          "",
        purchase_unit_price: String(
          item.purchaseUnitPrice ??
          item.purchase_unit_price ??
          0
        ),
      });
      return;
    }

    productLines.push({
      ...emptyProductLine(),
      catalog_item_id: item.catalogItemId || "",
      reference: item.reference || "",
      description: item.description || "",
      warranty_months: String(
        item.warrantyMonths ?? item.warranty_months ?? ""
      ),
      quantity: String(item.quantity || 1),
      unit_price: String(item.unitPrice ?? item.unit_price ?? ""),
      discount_rate: String(
        item.discountRate ?? item.discount_rate ?? 0
      ),
      sales_classification_code:
        item.salesClassificationCode ??
        item.sales_classification_code ??
        "",
      purchase_unit_price: String(
        item.purchaseUnitPrice ??
        item.purchase_unit_price ??
        0
      ),
    });
  });

  return {
    productLines:
      productLines.length > 0 ? productLines : [emptyProductLine()],
    handpieceLines:
      handpieceLines.length > 0
        ? handpieceLines
        : [emptyHandpieceLine()],
    serviceLines:
      serviceLines.length > 0 ? serviceLines : [emptyServiceLine()],
  };
};

const initialForm = (prospectId = "", opportunity = null) => ({
  prospect_id: prospectId,
  opportunity_id: opportunity?.id || "",
  evoliz_organization_code:
    opportunity?.evolizOrganizationCode || "",
  payment_terms:
    opportunity?.paymentTerms || "Paiement à définir",
  payment_method:
    opportunity?.payment?.method || "",
  business_process:
    opportunity?.businessProcess || "mixed",
  global_discount_rate: String(
    opportunity?.globalDiscountRate || 0
  ),
  notes: opportunity?.comment || "",
  ...opportunityInitialLines(opportunity),
});

const lineAmount = (line) => {
  const gross =
    Number(line.quantity || 0) *
    Number(line.unit_price || 0);
  const discount = Math.min(
    Math.max(Number(line.discount_rate || 0), 0),
    100
  );

  return gross * (1 - discount / 100);
};

const money = (value) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

function Field({ label, children }) {
  return (
    <label className="block min-w-0">
      <span className="text-xs font-bold text-[var(--do-muted)]">
        {label}
      </span>
      {children}
    </label>
  );
}

function RemoveButton({ onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--do-border)] text-[var(--do-muted)] transition hover:bg-[var(--do-accent-soft)] hover:text-[var(--do-accent)]"
    >
      <Trash size={17} />
    </button>
  );
}

export default function QuoteCreateForm({
  defaultProspectId = "",
  lockedProspect = null,
  opportunity = null,
  onCreated,
  onCancel,
}) {
  const [form, setForm] = useState(() =>
    initialForm(defaultProspectId, opportunity)
  );
  const [prospects, setProspects] = useState([]);
  const [recipientSearch, setRecipientSearch] = useState(
    lockedProspect ? prospectLabel(lockedProspect) : ""
  );
  const [selectedRecipient, setSelectedRecipient] = useState(
    lockedProspect || null
  );
  const [catalog, setCatalog] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [recipientOpen, setRecipientOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const locked = Boolean(lockedProspect?.id || defaultProspectId);

  const visibleProspects = useMemo(() => {
    if (lockedProspect?.id) {
      return [lockedProspect];
    }

    if (
      selectedRecipient?.id &&
      !prospects.some(
        (prospect) => prospect.id === selectedRecipient.id
      )
    ) {
      return [selectedRecipient, ...prospects];
    }

    return prospects;
  }, [lockedProspect, prospects, selectedRecipient]);

  const machineCatalog = useMemo(
    () =>
      catalog.filter(
        (item) =>
          !item.type ||
          item.type === "machine"
      ),
    [catalog]
  );

  const handpieceCatalog = useMemo(
    () => catalog.filter((item) => item.type === "handpiece"),
    [catalog]
  );

  useEffect(() => {
    setForm(
      initialForm(
        defaultProspectId || lockedProspect?.id || "",
        opportunity
      )
    );

    if (lockedProspect?.id) {
      setSelectedRecipient(lockedProspect);
      setRecipientSearch(prospectLabel(lockedProspect));
      setRecipientOpen(false);
    }
  }, [defaultProspectId, lockedProspect?.id, opportunity?.id]);

  useEffect(() => {
    let mounted = true;

    machineCatalogApi
      .list()
      .then((items) => {
        if (!mounted) return;
        setCatalog(
          Array.isArray(items)
            ? items.filter((item) => item.active !== false)
            : []
        );
      })
      .catch(() => {
        toast.error("Impossible de charger le catalogue.");
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (locked) {
      return undefined;
    }

    let active = true;

    const timer = window.setTimeout(() => {
      setLoadingProspects(true);

      prospectsApi
        .paginated({
          page: 1,
          limit: 20,
          search: recipientSearch.trim(),
          lifecycleStage: "all",
        })
        .then((data) => {
          if (!active) return;

          const items =
            data.items ||
            data.prospects ||
            data.data ||
            data ||
            [];

          setProspects(Array.isArray(items) ? items : []);
        })
        .catch((error) => {
          if (!active) return;

          toast.error(
            formatApiError(error.response?.data?.detail) ||
              "Impossible de rechercher les destinataires."
          );
        })
        .finally(() => {
          if (active) {
            setLoadingProspects(false);
          }
        });
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [locked, recipientSearch]);

  const updateRoot = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const chooseRecipient = (prospect) => {
    if (!prospect?.id) return;

    updateRoot("prospect_id", prospect.id);
    setSelectedRecipient(prospect);
    setRecipientSearch(prospectLabel(prospect));
    setRecipientOpen(false);
  };

  const updateLine = (group, index, key, value) => {
    setForm((current) => ({
      ...current,
      [group]: current[group].map((line, lineIndex) =>
        lineIndex === index
          ? {
              ...line,
              [key]: value,
            }
          : line
      ),
    }));
  };

  const removeLine = (group, index, emptyFactory) => {
    setForm((current) => {
      const next = current[group].filter(
        (_, lineIndex) => lineIndex !== index
      );

      return {
        ...current,
        [group]: next.length ? next : [emptyFactory()],
      };
    });
  };

  const addLine = (group, emptyFactory) => {
    setForm((current) => ({
      ...current,
      [group]: [...current[group], emptyFactory()],
    }));
  };

  const chooseCatalog = (group, index, catalogId) => {
    const item = catalog.find((entry) => entry.id === catalogId);

    setForm((current) => ({
      ...current,
      [group]: current[group].map((line, lineIndex) =>
        lineIndex === index
          ? {
              ...line,
              catalog_item_id: catalogId,
              description: item?.name || "",
              reference: item?.reference || "",
            }
          : line
      ),
    }));
  };

  const productSubtotal = useMemo(
    () =>
      form.productLines.reduce(
        (total, line) => total + lineAmount(line),
        0
      ),
    [form.productLines]
  );

  const handpieceSubtotal = useMemo(
    () =>
      form.handpieceLines.reduce(
        (total, line) => total + lineAmount(line),
        0
      ),
    [form.handpieceLines]
  );

  const serviceSubtotal = useMemo(
    () =>
      form.serviceLines.reduce(
        (total, line) => total + Number(line.unit_price || 0),
        0
      ),
    [form.serviceLines]
  );

  const subtotalBeforeGlobalDiscount =
    productSubtotal +
    handpieceSubtotal +
    serviceSubtotal;

  const globalDiscountRate = Math.min(
    Math.max(Number(form.global_discount_rate || 0), 0),
    100
  );
  const globalDiscountAmount =
    subtotalBeforeGlobalDiscount * globalDiscountRate / 100;
  const totalHt =
    subtotalBeforeGlobalDiscount - globalDiscountAmount;

  const vatAmount = totalHt * VAT_RATE / 100;
  const totalTtc = totalHt + vatAmount;

  const validateWarranty = (value) => {
    if (value === "" || value === null || value === undefined) {
      return true;
    }

    const months = Number(value);

    return (
      Number.isInteger(months) &&
      months >= 1 &&
      months <= 120
    );
  };

  const buildLines = () => {
    const products = form.productLines
      .filter(
        (line) =>
          line.catalog_item_id ||
          String(line.description || "").trim()
      )
      .map((line) => ({
        catalog_item_id: line.catalog_item_id || null,
        section: "Produit Principal",
        reference: String(line.reference || "").trim(),
        description: String(line.description || "").trim(),
        warranty_months:
          line.warranty_months === ""
            ? null
            : Number(line.warranty_months),
        quantity: Number(line.quantity || 0),
        unit: "Unité(s)",
        unit_price: Number(line.unit_price || 0),
        discount_rate: Number(line.discount_rate || 0),
        vat_rate: VAT_RATE,
        sales_classification_code: String(
          line.sales_classification_code || ""
        ).trim(),
        purchase_unit_price: Number(
          line.purchase_unit_price || 0
        ),
        notes: "",
      }));

    const handpieces = form.handpieceLines
      .filter(
        (line) =>
          line.catalog_item_id ||
          String(line.description || "").trim()
      )
      .map((line) => ({
        catalog_item_id: line.catalog_item_id || null,
        section: "Pièces à Main",
        reference: String(line.reference || "").trim(),
        description: String(line.description || "").trim(),
        warranty_months:
          line.warranty_months === ""
            ? null
            : Number(line.warranty_months),
        quantity: Number(line.quantity || 0),
        unit: "Unité(s)",
        unit_price: Number(line.unit_price || 0),
        discount_rate: Number(line.discount_rate || 0),
        vat_rate: VAT_RATE,
        sales_classification_code: String(
          line.sales_classification_code || ""
        ).trim(),
        purchase_unit_price: Number(
          line.purchase_unit_price || 0
        ),
        notes: "",
      }));

    const services = form.serviceLines
      .map((line) => {
        const description =
          line.service === "Autre"
            ? String(line.custom_description || "").trim()
            : String(line.service || "").trim();

        if (!description) return null;

        return {
          catalog_item_id: null,
          section: "Prestations incluses",
          reference: "",
          description,
          warranty_months: null,
          quantity: 1,
          unit: "Forfait",
          unit_price: Number(line.unit_price || 0),
          discount_rate: Number(line.discount_rate || 0),
          vat_rate: VAT_RATE,
          sales_classification_code: String(
            line.sales_classification_code || ""
          ).trim(),
          purchase_unit_price: Number(
            line.purchase_unit_price || 0
          ),
          notes: "",
        };
      })
      .filter(Boolean);

    return [...products, ...handpieces, ...services];
  };

  const submit = async (event) => {
    event.preventDefault();

    if (!String(form.prospect_id || "").trim()) {
      toast.error("Sélectionne un destinataire.");
      return;
    }

    const productLines = form.productLines.filter(
      (line) =>
        line.catalog_item_id ||
        String(line.description || "").trim()
    );

    if (!productLines.length) {
      toast.error("Ajoute au moins un produit principal.");
      return;
    }

    for (const line of [
      ...productLines,
      ...form.handpieceLines.filter(
        (item) =>
          item.catalog_item_id ||
          String(item.description || "").trim()
      ),
    ]) {
      if (!String(line.description || "").trim()) {
        toast.error("Chaque produit doit avoir un nom.");
        return;
      }

      if (!(Number(line.quantity) > 0)) {
        toast.error("La quantité doit être supérieure à zéro.");
        return;
      }

      if (Number(line.unit_price) < 0) {
        toast.error("Le prix HT ne peut pas être négatif.");
        return;
      }

      if (
        Number(line.discount_rate || 0) < 0 ||
        Number(line.discount_rate || 0) > 100
      ) {
        toast.error("La remise doit être comprise entre 0 et 100 %.");
        return;
      }

      if (Number(line.purchase_unit_price || 0) < 0) {
        toast.error("Le prix d'achat HT ne peut pas être négatif.");
        return;
      }

      if (!validateWarranty(line.warranty_months)) {
        toast.error(
          "La garantie doit être indiquée en mois, entre 1 et 120."
        );
        return;
      }
    }

    for (const service of form.serviceLines) {
      if (
        service.service === "Autre" &&
        !String(service.custom_description || "").trim()
      ) {
        toast.error("Précise le nom de la prestation.");
        return;
      }

      if (Number(service.unit_price || 0) < 0) {
        toast.error(
          "Le montant d'une prestation ne peut pas être négatif."
        );
        return;
      }

      if (
        Number(service.discount_rate || 0) < 0 ||
        Number(service.discount_rate || 0) > 100
      ) {
        toast.error("La remise doit être comprise entre 0 et 100 %.");
        return;
      }

      if (Number(service.purchase_unit_price || 0) < 0) {
        toast.error("Le prix d'achat HT ne peut pas être négatif.");
        return;
      }
    }

    if (
      Number(form.global_discount_rate || 0) < 0 ||
      Number(form.global_discount_rate || 0) > 100
    ) {
      toast.error("La remise globale doit être comprise entre 0 et 100 %.");
      return;
    }

    const lines = buildLines();
    const firstProduct = lines.find(
      (line) => line.section === "Produit Principal"
    );

    setSubmitting(true);

    try {
      const created = await quotesApi.create({
        prospect_id: form.prospect_id,
        opportunity_id:
          opportunity?.id ||
          form.opportunity_id ||
          null,
        machine_catalog_id:
          firstProduct?.catalog_item_id || null,
        machine_name:
          firstProduct?.description || "",
        amount: totalHt,
        currency: "EUR",
        vat_rate: VAT_RATE,
        include_terms: true,
        evoliz_organization_code:
          form.evoliz_organization_code.trim(),
        payment_terms: form.payment_terms.trim(),
        payment_method: form.payment_method,
        business_process: form.business_process,
        global_discount_rate: Number(
          form.global_discount_rate || 0
        ),
        notes: form.notes.trim(),
        lines,
      });

      toast.success("Devis créé.");

      setForm(
        initialForm(
          defaultProspectId || lockedProspect?.id || "",
          opportunity
        )
      );

      await onCreated?.(
        created?.quote || created
      );
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) ||
          error.message ||
          "Création du devis impossible."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="lirah-card space-y-6 p-5"
      data-testid="quote-create-form"
    >
      <div>
        <h2 className="font-heading text-xl font-black text-[var(--do-ink)]">
          Nouveau devis
        </h2>
      </div>

      <section className="space-y-3">
        <h3 className="text-xs font-black uppercase tracking-[0.08em] text-[var(--do-muted)]">
          Destinataire
        </h3>

        <div className="relative">
          <Field label="Rechercher un prospect ou client">
            <input
              type="search"
              value={recipientSearch}
              readOnly={locked}
              onFocus={() => {
                if (!locked) setRecipientOpen(true);
              }}
              onChange={(event) => {
                const value = event.target.value;
                setRecipientSearch(value);
                updateRoot("prospect_id", "");
                setSelectedRecipient(null);
                setRecipientOpen(true);
              }}
              placeholder="Nom, société, email, téléphone…"
              autoComplete="off"
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-sm"
              data-testid="quote-recipient-search"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={!locked && recipientOpen}
            />
          </Field>

          {!locked && recipientOpen && (
            <div
              className="absolute left-0 right-0 top-full z-40 mt-1 max-h-64 overflow-y-auto rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-1 shadow-xl"
              role="listbox"
              data-testid="quote-recipient-results"
            >
              {loadingProspects ? (
                <div className="px-3 py-3 text-sm text-[var(--do-muted)]">
                  Recherche…
                </div>
              ) : visibleProspects.length ? (
                visibleProspects.map((prospect) => (
                  <button
                    key={prospect.id}
                    type="button"
                    role="option"
                    className="flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm font-bold text-[var(--do-ink)] hover:bg-[var(--do-surface-soft)] focus:bg-[var(--do-surface-soft)] focus:outline-none"
                    onMouseDown={(event) => {
                      event.preventDefault();
                      chooseRecipient(prospect);
                    }}
                    data-testid="quote-recipient-option"
                  >
                    {prospectLabel(prospect)}
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-sm text-[var(--do-muted)]">
                  Aucun destinataire trouvé
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section
        className="border-t border-[var(--do-border)] pt-5"
        data-testid="quote-evoliz-fields"
      >
        <h3 className="mb-4 text-xs font-black uppercase tracking-[0.08em] text-[var(--do-muted)]">
          Informations devis et facturation
        </h3>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Code organisation Evoliz (optionnel)">
            <input
              value={form.evoliz_organization_code}
              onChange={(event) =>
                updateRoot(
                  "evoliz_organization_code",
                  event.target.value
                )
              }
              placeholder="Ex. DUPONT001"
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
            />
          </Field>

          <Field label="Conditions de règlement">
            <input
              value={form.payment_terms}
              onChange={(event) =>
                updateRoot("payment_terms", event.target.value)
              }
              placeholder="Ex. 30 jours"
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
            />
          </Field>

          <Field label="Mode de paiement">
            <select
              value={form.payment_method}
              onChange={(event) =>
                updateRoot("payment_method", event.target.value)
              }
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
            >
              {PAYMENT_METHOD_OPTIONS.map(([value, label]) => (
                <option key={value || "undefined"} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Nature des opérations">
            <select
              value={form.business_process}
              onChange={(event) =>
                updateRoot("business_process", event.target.value)
              }
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
            >
              {BUSINESS_PROCESS_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Remise globale (%)">
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={form.global_discount_rate}
              onChange={(event) =>
                updateRoot(
                  "global_discount_rate",
                  event.target.value
                )
              }
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
            />
          </Field>

          <Field label="Commentaires (optionnel)">
            <textarea
              rows={3}
              value={form.notes}
              onChange={(event) =>
                updateRoot("notes", event.target.value)
              }
              className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
            />
          </Field>
        </div>
      </section>

      <section
        className="border-t border-[var(--do-border)] pt-5"
        data-testid="quote-main-products"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h3 className="font-heading text-sm font-black uppercase tracking-[0.06em] text-[var(--do-ink)]">
            Produit principal
          </h3>

          <strong className="font-mono text-sm text-[var(--do-ink)]">
            {money(productSubtotal)}
          </strong>
        </div>

        <div className="space-y-4">
          {form.productLines.map((line, index) => (
            <div
              key={`product-${index}`}
              className="rounded-2xl border border-[var(--do-border)] p-4"
              data-testid={`quote-main-product-${index}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="grid min-w-0 flex-1 gap-3 md:grid-cols-3">
                  <Field label="Produit">
                    <select
                      required={index === 0}
                      value={line.catalog_item_id}
                      onChange={(event) =>
                        chooseCatalog(
                          "productLines",
                          index,
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    >
                      <option value="">
                        Choisir dans le catalogue
                      </option>

                      {machineCatalog.map((item) => (
                        <option
                          key={item.id}
                          value={item.id}
                        >
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Référence (optionnelle)">
                    <input
                      value={line.reference}
                      onChange={(event) =>
                        updateLine(
                          "productLines",
                          index,
                          "reference",
                          event.target.value
                        )
                      }
                      placeholder="Optionnelle"
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Garantie en mois (optionnelle)">
                    <input
                      type="number"
                      min="1"
                      max="120"
                      step="1"
                      value={line.warranty_months}
                      onChange={(event) =>
                        updateLine(
                          "productLines",
                          index,
                          "warranty_months",
                          event.target.value
                        )
                      }
                      placeholder="Optionnelle"
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Quantité">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={line.quantity}
                      onChange={(event) =>
                        updateLine(
                          "productLines",
                          index,
                          "quantity",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

<Field label="Prix HT">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(event) =>
                        updateLine(
                          "productLines",
                          index,
                          "unit_price",
                          event.target.value
                        )
                      }
                      placeholder="0,00"
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Remise (%)">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line.discount_rate}
                      onChange={(event) =>
                        updateLine(
                          "productLines",
                          index,
                          "discount_rate",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Code classification vente (optionnel)">
                    <input
                      value={line.sales_classification_code}
                      onChange={(event) =>
                        updateLine(
                          "productLines",
                          index,
                          "sales_classification_code",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Prix d'achat HT (optionnel)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.purchase_unit_price}
                      onChange={(event) =>
                        updateLine(
                          "productLines",
                          index,
                          "purchase_unit_price",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>
                </div>

                {form.productLines.length > 1 && (
                  <RemoveButton
                    label="Supprimer le produit principal"
                    onClick={() =>
                      removeLine(
                        "productLines",
                        index,
                        emptyProductLine
                      )
                    }
                  />
                )}
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() =>
            addLine(
              "productLines",
              emptyProductLine
            )
          }
          className="mt-4 inline-flex items-center gap-2 text-sm font-black text-[var(--do-accent)]"
          data-testid="quote-add-main-product"
        >
          <Plus size={17} />
          Ajouter un produit principal
        </button>
      </section>

      <section
        className="border-t border-[var(--do-border)] pt-5"
        data-testid="quote-handpieces"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h3 className="font-heading text-sm font-black uppercase tracking-[0.06em] text-[var(--do-ink)]">
            Pièces à main
          </h3>

          <strong className="font-mono text-sm text-[var(--do-ink)]">
            {money(handpieceSubtotal)}
          </strong>
        </div>

        <div className="space-y-4">
          {form.handpieceLines.map((line, index) => (
            <div
              key={`handpiece-${index}`}
              className="rounded-2xl border border-[var(--do-border)] p-4"
              data-testid={`quote-handpiece-${index}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="grid min-w-0 flex-1 gap-3 md:grid-cols-3">
                  <Field label="Pièce à main">
                    <select
                      value={line.catalog_item_id}
                      onChange={(event) =>
                        chooseCatalog(
                          "handpieceLines",
                          index,
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    >
                      <option value="">
                        Choisir dans le catalogue
                      </option>

                      {handpieceCatalog.map((item) => (
                        <option
                          key={item.id}
                          value={item.id}
                        >
                          {item.name}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field label="Référence (optionnelle)">
                    <input
                      value={line.reference}
                      onChange={(event) =>
                        updateLine(
                          "handpieceLines",
                          index,
                          "reference",
                          event.target.value
                        )
                      }
                      placeholder="Optionnelle"
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Garantie en mois (optionnelle)">
                    <input
                      type="number"
                      min="1"
                      max="120"
                      step="1"
                      value={line.warranty_months}
                      onChange={(event) =>
                        updateLine(
                          "handpieceLines",
                          index,
                          "warranty_months",
                          event.target.value
                        )
                      }
                      placeholder="Optionnelle"
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Quantité">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={line.quantity}
                      onChange={(event) =>
                        updateLine(
                          "handpieceLines",
                          index,
                          "quantity",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

<Field label="Prix HT">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(event) =>
                        updateLine(
                          "handpieceLines",
                          index,
                          "unit_price",
                          event.target.value
                        )
                      }
                      placeholder="0,00"
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Remise (%)">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line.discount_rate}
                      onChange={(event) =>
                        updateLine(
                          "handpieceLines",
                          index,
                          "discount_rate",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Code classification vente (optionnel)">
                    <input
                      value={line.sales_classification_code}
                      onChange={(event) =>
                        updateLine(
                          "handpieceLines",
                          index,
                          "sales_classification_code",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Prix d'achat HT (optionnel)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.purchase_unit_price}
                      onChange={(event) =>
                        updateLine(
                          "handpieceLines",
                          index,
                          "purchase_unit_price",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>
                </div>

                <RemoveButton
                  label="Supprimer la pièce à main"
                  onClick={() =>
                    removeLine(
                      "handpieceLines",
                      index,
                      emptyHandpieceLine
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() =>
            addLine(
              "handpieceLines",
              emptyHandpieceLine
            )
          }
          className="mt-4 inline-flex items-center gap-2 text-sm font-black text-[var(--do-accent)]"
          data-testid="quote-add-handpiece"
        >
          <Plus size={17} />
          Ajouter une pièce à main
        </button>
      </section>

      <section
        className="border-t border-[var(--do-border)] pt-5"
        data-testid="quote-services"
      >
        <div className="mb-4 flex items-center justify-between gap-4">
          <h3 className="font-heading text-sm font-black uppercase tracking-[0.06em] text-[var(--do-ink)]">
            Prestations incluses
          </h3>

          <strong className="font-mono text-sm text-[var(--do-ink)]">
            {money(serviceSubtotal)}
          </strong>
        </div>

        <div className="space-y-4">
          {form.serviceLines.map((line, index) => (
            <div
              key={`service-${index}`}
              className="rounded-2xl border border-[var(--do-border)] p-4"
              data-testid={`quote-service-${index}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="grid min-w-0 flex-1 gap-3 md:grid-cols-3">
                  <Field label="Prestation">
                    <select
                      value={line.service}
                      onChange={(event) =>
                        updateLine(
                          "serviceLines",
                          index,
                          "service",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    >
                      <option value="">
                        Choisir une prestation
                      </option>

                      {SERVICE_OPTIONS.map((service) => (
                        <option
                          key={service}
                          value={service}
                        >
                          {service}
                        </option>
                      ))}
                    </select>
                  </Field>

                  {line.service === "Autre" && (
                    <Field label="Autre prestation">
                      <input
                        value={line.custom_description}
                        onChange={(event) =>
                          updateLine(
                            "serviceLines",
                            index,
                            "custom_description",
                            event.target.value
                          )
                        }
                        placeholder="Nom de la prestation"
                        className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                      />
                    </Field>
                  )}

                  <Field label="Montant HT">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.unit_price}
                      onChange={(event) =>
                        updateLine(
                          "serviceLines",
                          index,
                          "unit_price",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Remise (%)">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={line.discount_rate}
                      onChange={(event) =>
                        updateLine(
                          "serviceLines",
                          index,
                          "discount_rate",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Code classification vente (optionnel)">
                    <input
                      value={line.sales_classification_code}
                      onChange={(event) =>
                        updateLine(
                          "serviceLines",
                          index,
                          "sales_classification_code",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>

                  <Field label="Prix d'achat HT (optionnel)">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={line.purchase_unit_price}
                      onChange={(event) =>
                        updateLine(
                          "serviceLines",
                          index,
                          "purchase_unit_price",
                          event.target.value
                        )
                      }
                      className="mt-1 w-full rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3 py-2.5 text-sm"
                    />
                  </Field>
                </div>

                <RemoveButton
                  label="Supprimer la prestation"
                  onClick={() =>
                    removeLine(
                      "serviceLines",
                      index,
                      emptyServiceLine
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={() =>
            addLine(
              "serviceLines",
              emptyServiceLine
            )
          }
          className="mt-4 inline-flex items-center gap-2 text-sm font-black text-[var(--do-accent)]"
          data-testid="quote-add-service"
        >
          <Plus size={17} />
          Ajouter une prestation
        </button>
      </section>

      <section className="border-t border-[var(--do-border)] pt-5">
        <div className="ml-auto max-w-md space-y-2 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-[var(--do-muted)]">
              Sous-total HT
            </span>
            <strong className="font-mono">
              {money(subtotalBeforeGlobalDiscount)}
            </strong>
          </div>

          {globalDiscountAmount > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-[var(--do-muted)]">
                Remise globale ({globalDiscountRate} %)
              </span>
              <strong className="font-mono">
                - {money(globalDiscountAmount)}
              </strong>
            </div>
          )}

          <div className="flex justify-between gap-4">
            <span className="text-[var(--do-muted)]">
              Montant hors taxes
            </span>
            <strong className="font-mono">
              {money(totalHt)}
            </strong>
          </div>

          <div className="flex justify-between gap-4">
            <span className="text-[var(--do-muted)]">
              TVA (20 %)
            </span>
            <strong className="font-mono">
              {money(vatAmount)}
            </strong>
          </div>

          <div className="flex justify-between gap-4 border-t border-[var(--do-border)] pt-3 text-base">
            <span className="font-black">
              Total TTC
            </span>
            <strong className="font-mono text-lg text-[var(--do-accent)]">
              {money(totalTtc)}
            </strong>
          </div>
        </div>
      </section>

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          disabled={submitting}
          onClick={onCancel}
          className="inline-flex items-center justify-center rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-5 py-3 text-sm font-black text-[var(--do-ink)] disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="quote-create-cancel"
        >
          Annuler
        </button>

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center rounded-xl bg-[var(--do-accent-strong)] px-5 py-3 text-sm font-black text-[var(--do-on-accent)] disabled:cursor-not-allowed disabled:opacity-60"
          data-testid="quote-create-submit"
        >
          {submitting
            ? "Création…"
            : "Créer le devis"}
        </button>
      </div>
    </form>
  );
}
